import { isValidSettingKey, SEGMENTATION_SETTINGS_SCHEMA, SegmentationSettingsOptions } from '@src/components/segmentation/segmentation-typing';
import { useSegmentationSettingsStore } from '@src/store/contours/contour-segmentation';
import { HeaderDisplayConfig, HeaderDisplayInfo, useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';
import { ThemeMode, useAppState, UserSettings } from '@src/store/ocg-app-state';
import { computed, reactive, ref } from 'vue';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useOrthancStore } from '../store/dicom-web/orthanc-store';

// ── Offset helpers ──────────────────────────────────────────────────────────
const OFFSET_OPTIONS = Array.from({ length: 11 }, (_, i) => i * 10); // 0-100 step 10

function validateOffset(offset: unknown): number {
  const num = typeof offset === 'string' ? Number.parseInt(offset, 10) : offset;
  if (
    typeof num === 'number'
    && !Number.isNaN(num)
    && OFFSET_OPTIONS.includes(num)
  ) {
    return num;
  }
  return 10; // default
}

// ── Shared reactive state at module-level ────────────────────────────────────
const internalEndoOffset = ref(10);
const internalEpiOffset = ref(10);

const segmentationSettingsInternal = reactive<Record<string, string>>({});

const selectedHeaderKeys = ref<{ key: keyof HeaderDisplayInfo; label: string; enabled: boolean; default: boolean }[]>([]);

const themeMode = ref<ThemeMode>('dark');
const networkCompression = ref(false);

const isInitialized = ref(false);
let settingsSnapshot: string | null = null;

// ── Composable ──────────────────────────────────────────────────────────────
export function useAppSettings() {
  const appState = useAppState();
  const viewportStateStore = useViewportStateStore();
  const segmentationSettingsStore = useSegmentationSettingsStore();
  const currentStudyStore = useCurrentStudyStore();

  const activeViewers = computed(() => appState.activeViewers);

  // Dynamic computed properties linked to the active store
  const patientHeaderInfo = computed(() => currentStudyStore.getHeaderDisplayInfo);
  const currentActiveHeaderInfo = computed(() => currentStudyStore.getActiveHeaderDisplayInfo);

  const patientHeaderOptions = computed(() =>
    Object.entries(patientHeaderInfo.value).map(
      ([key, info]) => ({
        key,
        label: info.label,
        enabled: info.enabled,
        default: info.default,
      }),
    ),
  );

  // Initialize once if not done yet
  const initialize = () => {
    if (isInitialized.value) return;

    // Offsets
    const currentEndo = viewportStateStore.globalOffsets.saxLvTransmuralityOffsetEndo;
    internalEndoOffset.value = (currentEndo !== null && currentEndo !== -1)
      ? currentEndo
      : validateOffset(viewportStateStore.getSaxLvTransmuralityOffsetEndo(activeViewers.value[0]));

    const currentEpi = viewportStateStore.globalOffsets.saxLvTransmuralityOffsetEpi;
    internalEpiOffset.value = (currentEpi !== null && currentEpi !== -1)
      ? currentEpi
      : validateOffset(viewportStateStore.getSaxLvTransmuralityOffsetEpi(activeViewers.value[0]));

    // Segmentation settings
    const savedSegSettings = segmentationSettingsStore.getSettings();

    function mapSavedOptionToLabel<K extends SegmentationSettingsOptions>(key: K): string | null {
      const setting = SEGMENTATION_SETTINGS_SCHEMA[key];
      if (!setting) return null;
      const savedValue = savedSegSettings[key];
      const index = setting.optionsRaw.indexOf(savedValue);
      return index >= 0 ? setting.optionLabels[index] : null;
    }

    for (const k in segmentationSettingsInternal) {
      delete segmentationSettingsInternal[k];
    }
    Object.assign(
      segmentationSettingsInternal,
      Object.fromEntries<string>(
        Object.keys(SEGMENTATION_SETTINGS_SCHEMA)
          .map(
            (key): [SegmentationSettingsOptions, string] | null => {
              if (!isValidSettingKey(key)) return null;
              const label = mapSavedOptionToLabel(key);
              if (label === null) return null;
              return [key, label];
            },
          )
          .filter((x): x is [SegmentationSettingsOptions, string] => x !== null),
      ),
    );

    // Patient info headers
    selectedHeaderKeys.value = (Object.entries(currentActiveHeaderInfo.value) as [keyof HeaderDisplayInfo, HeaderDisplayConfig][])
      .filter(([, info]) => info.enabled)
      .map(([key]) => ({
        key,
        label: patientHeaderInfo.value[key]?.label ?? '',
        enabled: true,
        default: patientHeaderInfo.value[key]?.default ?? false,
      }));

    // Theme mode
    themeMode.value = appState.currentTheme === 'dark' ? 'dark' : 'light';

    // Network compression
    networkCompression.value = false;

    isInitialized.value = true;
  };

  initialize();

  // ── Apply helpers (write reactive state → stores) ─────────────────────
  function applyEndoEpiOffsets() {
    internalEpiOffset.value = validateOffset(internalEpiOffset.value);
    internalEndoOffset.value = validateOffset(internalEndoOffset.value);
    viewportStateStore.setSaxLvTransmuralityOffsetsGlobal(
      internalEpiOffset.value,
      internalEndoOffset.value,
    );
  }

  function applyTheme() {
    appState.setColorScheme(themeMode.value);
  }

  function applySegmentation() {
    segmentationSettingsStore.updateSettingsFromLabels(
      segmentationSettingsInternal,
    );
  }

  function applyPatientHeaders() {
    const updatedHeaderInfo: Record<string, boolean> = {};
    Object.keys(patientHeaderInfo.value).forEach((key) => {
      updatedHeaderInfo[key] = false;
    });
    selectedHeaderKeys.value.forEach((item) => {
      updatedHeaderInfo[item.key] = true;
    });
    currentStudyStore.updateActiveDisplayHeaderInfo(updatedHeaderInfo);
  }

  function applyNetwork() {
    const orthancStore = useOrthancStore();
    orthancStore.useCompression = networkCompression.value;
  }

  /** Apply every settings section to the underlying stores */
  function applyAll() {
    applyEndoEpiOffsets();
    applyTheme();
    applySegmentation();
    applyPatientHeaders();
    applyNetwork();
  }

  // ── Serialise / Deserialise ───────────────────────────────────────────
  function serialize(): UserSettings {
    return {
      analysis: {
        endoOffset: internalEndoOffset.value,
        epiOffset: internalEpiOffset.value,
      },
      theme: {
        mode: themeMode.value,
      },
      segmentation: { ...segmentationSettingsInternal },
      patient: {
        selectedKeys: selectedHeaderKeys.value.map(item => item.key),
      },
      network: {
        compression: networkCompression.value,
      },
    };
  }

  function applyFromJSON(json: Partial<UserSettings>) {
    if (json.analysis) {
      internalEndoOffset.value = json.analysis.endoOffset;
      internalEpiOffset.value = json.analysis.epiOffset;
      applyEndoEpiOffsets();
    }
    if (json.theme) {
      themeMode.value = json.theme.mode;
      applyTheme();
    }
    if (json.segmentation) {
      Object.assign(segmentationSettingsInternal, json.segmentation);
      applySegmentation();
    }

    function isValidHeaderKey(key: string): key is (keyof HeaderDisplayInfo) {
      return key in patientHeaderInfo.value;
    }

    if (json.patient) {
      const validHeaderKeys = json.patient.selectedKeys.filter(isValidHeaderKey);
      selectedHeaderKeys.value = validHeaderKeys.map(
        (key) => {
          const info = patientHeaderInfo.value[key];
          return {
            key,
            label: info?.label,
            enabled: true,
            default: info?.default,
          };
        },
      );
    }
    if (json.network) {
      networkCompression.value = !!json.network.compression;
      applyNetwork();
    }
    applyPatientHeaders();
  }

  // Snapshot mechanism
  function takeSnapshot() {
    settingsSnapshot = JSON.stringify(serialize());
  }

  function restoreSnapshot() {
    if (settingsSnapshot) {
      applyFromJSON(JSON.parse(settingsSnapshot) as Partial<UserSettings>);
    }
  }

  // ── Public API ────────────────────────────────────────────────────────
  /**
   * Fetch the user's persisted settings from supabase and apply them.
   * Safe to call even when there is no active session (silently returns).
   */
  async function loadSettings() {
    const userSettings = await appState.retrieveUserSettings();
    if (userSettings) {
      applyFromJSON(userSettings);
    }
  }

  /**
   * Persist current settings to supabase and apply them to all stores.
   */
  async function saveSettings() {
    const json = serialize();
    await appState.updateUserAppSettings(json);
    applyAll();
  }

  return {
    // reactive state (bind in dialog UI)
    internalEndoOffset,
    internalEpiOffset,
    segmentationSettingsInternal,
    patientHeaderInfo,
    currentActiveHeaderInfo,
    patientHeaderOptions,
    selectedHeaderKeys,
    themeMode,
    networkCompression,

    // constants
    offsetOptions: OFFSET_OPTIONS,

    // actions
    loadSettings,
    saveSettings,
    applyAll,
    applyFromJSON,
    serialize,
    takeSnapshot,
    restoreSnapshot,
  };
}
